import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Snackbar, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import ContainerInterior from './ContainerPlanning';
import PalletFillVisual from './PalletFillVisual';
import { ContainerFillResult } from '../../../app/services/api/orderManagementClient';
import { applyRoundAndFormat } from '../../../utils';
import { useTranslation } from 'react-i18next';

interface ContainerPlanningViewProps {
    containerData: ContainerFillResult | null;
    currencyCode: string;
    deliveryChargeTotal?: string;
}

const ContainerPlanningView: React.FC<ContainerPlanningViewProps> = ({ containerData, currencyCode, deliveryChargeTotal }) => {

  const [showContainerSnackbar, setShowContainerSnackbar] = useState(false);
  const prevContainerCount = useRef<number>(0);
  const { t } = useTranslation('orderDetails');

  useEffect(() => {
    if (containerData) {
      const currentCount = containerData.containerFillPercents.length;
      if (prevContainerCount.current && currentCount > prevContainerCount.current) {
        setShowContainerSnackbar(true);
      }
      prevContainerCount.current = currentCount;
    }
  }, [containerData?.containerFillPercents.length]);

  if (!containerData) {
    return <Typography>Loading container data...</Typography>;
  }

  const MAX_PALLETS_PER_CONTAINER = containerData.maxPalletsPerContainer;
  const isPalletOnlyMode = MAX_PALLETS_PER_CONTAINER === 1;

  return (
    <Box
      display="flex"
      gap={3}
      sx={{
        width: '100%',
        overflowX: 'auto', // allow horizontal scroll
      }}
    >
      <Box
        display="flex" // <-- use "flex" not "inline-flex"
        gap={3}
        flexWrap="nowrap"
        sx={{
          minWidth: '100%',
        }}
      >
        {containerData.containerFillPercents.map((percent, index) => {
          const palletsInContainer = Math.round(
            (percent / 100) * MAX_PALLETS_PER_CONTAINER,
          );
          const isFilledEnough = percent >= containerData.minFill;
          const isLastPallet = index === containerData.containerFillPercents.length - 1;
          const palletDeliveryCharge = isLastPallet ? (parseFloat(deliveryChargeTotal ?? '0') || 0) : 0;

          const borderColor = isFilledEnough ? '#4CAF50' : '#FF9800';
          const backgroundColor = isFilledEnough ? '#A8E6A1' : '#FFE082';

          return (
            <Box
              key={index}
              display="flex"
              flexDirection="column"
              alignItems="center"
              minWidth="250px"
            >
              <Typography
                gutterBottom>
                {isPalletOnlyMode ? t('containerPlanning.pallet') : t('containerPlanning.container')} #{index + 1}
              </Typography>

              <Box
                border={`4px solid ${borderColor}`}
                bgcolor={backgroundColor}
                p={2}
                mb={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="100%"
              >
                {isPalletOnlyMode ? (
                  <PalletFillVisual
                    fillPercent={percent}
                    minFill={containerData.minFill}
                  />
                ) : (
                  <ContainerInterior palletNo={palletsInContainer} />
                )}
              </Box>

              <Table
                size="small"
                sx={{
                  borderCollapse: 'collapse',
                  '& td, & th': {
                    border: '2px solid black',
                    fontWeight: 'bold',
                  },
                  width: '100%',
                }}
              >
                <TableBody>
                  {!isPalletOnlyMode && (
                    <TableRow>
                      <TableCell>{t('containerPlanning.containerSizeFt')}</TableCell>
                      <TableCell>{containerData.containerType}</TableCell>
                    </TableRow>
                  )}
                  {!isPalletOnlyMode && (
                    <TableRow>
                      <TableCell>{t('containerPlanning.palletSize')}</TableCell>
                      <TableCell>{containerData.palletType}</TableCell>
                    </TableRow>
                  )}
                  {!isPalletOnlyMode && (
                    <TableRow>
                      <TableCell>{t('containerPlanning.palletFilled')}</TableCell>
                      <TableCell>
                        {palletsInContainer} / {MAX_PALLETS_PER_CONTAINER}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell>{t('containerPlanning.minFill')} %</TableCell>
                    <TableCell>{containerData.minFill}%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('containerPlanning.currentFill')} %</TableCell>                    <TableCell
                      sx={{
                        backgroundColor: isFilledEnough ? '#A8E6A1' : '#FFE082',
                      }}
                    >
                      {percent.toFixed(2)}%
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('containerPlanning.deliveryCharge')}</TableCell>
                    <TableCell>{applyRoundAndFormat(palletDeliveryCharge, currencyCode)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          );
        })}
      </Box>
      <Snackbar
        open={showContainerSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowContainerSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowContainerSnackbar(false)}
          severity={'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {isPalletOnlyMode ? t('containerPlanning.newPalletAdded') : t('containerPlanning.newContainerAdded')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContainerPlanningView;